import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Textarea } from 'rizzui';
import * as yup from 'yup';
import CustomSelect from '../../components/forms/CustomSelect';
import CustomSelectApiHookForm from '../../components/forms/CustomSelectApiHookForms';
import RequiredInput from '../../components/forms/InputHOC';
import useIncidenceTypes from '../../hooks/api-calls/Incidences/useIncidenceTypes';
import useIncidenceUsers from '../../hooks/api-calls/Incidences/useIncidenceUsers';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { setContractId } from '../../redux/contractProfileSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { Incidence } from '../../type/entities/incidence-type';
import FormFooter from '../_layout/_footers/form-footer';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import useCompanies from '../../hooks/api-calls/useCompanies';

interface IncidenceFormProps {
    isLoading: boolean;
    submit: Function;
    data?: Incidence | undefined;
}

const schema = yup.object({
    description: yup.string().required('La descripción es obligatoria'),
    incidenceTypeId: yup.string().required('El tipo de incidencia es obligatorio'),
    contractId: yup.string().notRequired(),
    participants: yup.array().notRequired(),
    managerId: yup.string().notRequired(),
});
// Para superadmin, tenermos que añadir la compañia
const superAdminSchema = yup.object({
    company: yup.string().required('La compañia es obligatoria'),
});

const IncidenceForm: React.FC<IncidenceFormProps> = ({ isLoading, submit, data }) => {

    const { contractId } = useSelector((state: RootState) => state.contractProfile);
    const { contractId: contractIdFromRoute } = useParams<{ contractId?: string }>();
    const mode = data ? 'Editar' : 'Crear';
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const { getIncidenceTypesList } = useIncidenceTypes();
    const { fetchIncidenceUsersData } = useIncidenceUsers();
    const { getCompaniesList } = useCompanies();
    const { userCan } = usePrivilege();
;

    const formik = useFormik({
        initialValues: {
            incidenceId: data?.id || '',
            incidenceTypeId: data?.incidenceType?.id || '',
            managerId: data?.manager?.id || '',
            managerName: data?.manager?.name || '',
            contractId: data?.contract?.code || '',
            participants: data?.users?.map((u: any) => u.user.id) || [],
            participantsName: data?.users?.map((u: any) => u.user.name) || [],
            subject: data?.subject || '',
            description: data?.description || '',
            company: data?.company?.id || '',
        },
        validationSchema: userCan('list_company', 'companies') ? schema.concat(superAdminSchema) : schema,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: values => { submit(values) },
    });
    const { setFieldValue } = formik;

    const [hideContractField, setHideContractField] = useState(false);

    useEffect(() => {
        const resolvedContractId = contractId || contractIdFromRoute;

        if (mode === 'Crear' && resolvedContractId) {
            setFieldValue('contractId', resolvedContractId);
            setHideContractField(true);
            dispatch(setContractId(null));
        }
    }, [contractId, contractIdFromRoute, mode, dispatch, setFieldValue]);

    const getContent = () => {
        return (
            <div className="@container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 font-medium">
                        <FormGroup
                            title="Información General"
                            description="Datos principales"
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-12"
                        >
                            <Textarea
                                id='description'
                                label={<label>Descripción <span className="text-red-500"> *</span></label>}
                                className="[&>label>span]:font-medium col-span-12 md:col-span-6"
                                onChange={formik.handleChange}
                                value={formik.values.description}
                                error={formik.errors.description}
                            />

                            <CustomSelect
                                isSearchable
                                isClearable
                                id={'incidenceTypeId'}
                                label="Tipo de Incidencia"
                                value={getIncidenceTypesList()?.find((g: any) => g.value === formik.values.incidenceTypeId)}
                                options={getIncidenceTypesList()}
                                onChange={(e: any) => {
                                    const selectedTypeName = e?.label || '';
                                    const isOtherType = selectedTypeName.trim().toLowerCase() === 'otro';
                                    const selectedDescription = isOtherType ? '' : (e?.description || '');

                                    formik.setFieldValue('incidenceTypeId', e?.value || '');
                                    formik.setFieldValue('subject', selectedTypeName);
                                    formik.setFieldValue('description', selectedDescription);
                                }}
                                required
                                error={formik.errors.incidenceTypeId}
                                containerClassName='col-span-12 md:col-span-4'
                            />

                            {!hideContractField && (
                                <RequiredInput
                                    required={false}
                                    id="contractId"
                                    label="Contrato"
                                    formik={formik}
                                    className="col-span-12 md:col-span-4"
                                />
                            )}

                            <CustomSelectApiHookForm
                                id="managerId"
                                label="Responsable"
                                formik={formik}
                                containerClassName='col-span-12 md:col-span-4'
                                fetchOptions={async (searchText: string | null) => {
                                    const text = searchText || formik.values.managerName;
                                    return fetchIncidenceUsersData({ search_text: text });
                                }}
                                error={formik.errors.managerId}
                            />

                            <CustomSelectApiHookForm
                                id="participants"
                                label="Participantes"
                                isMulti
                                formik={formik}
                                containerClassName='col-span-12 md:col-span-6'
                                fetchOptions={async (searchText: string | null) => {
                                    const text = searchText || formik.values.participantsName;
                                    return fetchIncidenceUsersData({ search_array: text });
                                }}
                                error={formik.errors.managerId}
                            />
                            {userCan('list_company', 'companies') && (
                                <CustomSelect
                                    id="companyId"
                                    label="Compañia"
                                    isSearchable
                                    placeholder="una empresa..."
                                    options={getCompaniesList()}
                                    value={getCompaniesList()?.find((g: any) => g.value === formik.values.company)}
                                    onChange={(selectedOptions: any) => {
                                        formik.setFieldValue('company', selectedOptions?.value);
                                    }}
                                    error={formik.errors.company}
                                    noOption={() => 'No hay empresas disponibles'}
                                    containerClassName='col-span-12 md:col-span-4'
                                />
                            )}
                        </FormGroup>
                    </div>
                </form>
            </div>
        )
    };

    return (
        <>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Incidencia'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </>
    );
};

export default IncidenceForm;